import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import Cover from "./assets/password-cover.svg";
import WhiteLogo from "./assets/white-logo.svg";
import TestInformation from "./TestInformation";

/**
 * 學生 dashboard——一場模考一個入口。
 *
 * 原本學生會收到三條各自獨立的連結（聽力／閱讀／寫作），想先考哪科都行。
 * 現在一封信只有一條總連結、一組總密碼，進來看到三張科目卡片，
 * 正式模考時 L→R→W 依序解鎖。
 *
 * 三個階段：
 *   password → sections → info（說明影片＋Confirm）→ 交給既有的考試流程
 *
 * 考完一科回到這裡時，密碼由 App 記著（savedPassword），直接跳過密碼畫面
 * 重新抓一次科目狀態——剛考完的那科變成已完成、下一科解鎖。同一組密碼
 * 只需要輸入一次。
 *
 * 「依序解鎖」不是靠這裡把卡片變灰——伺服器根本不會把未解鎖科目的 url_key
 * 送過來，而且真正要進場時 getExamByUrlKey 會再擋一次。這裡只是把狀態畫出來。
 */

const StyledDashboard = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  background-image: url(${Cover});
  background-size: 100%;
  background-repeat: repeat-y;
  color: #fff;
  font-family: Arial, "Noto Sans TC", sans-serif;

  .inner {
    max-width: 900px;
    margin: 0 auto;
    padding: 6vh 24px 8vh;
  }

  .logo {
    display: block;
    width: 260px;
    max-width: 60%;
    margin: 0 auto 32px;
  }

  .who {
    text-align: center;
    margin-bottom: 36px;
    h1 {
      font-size: 26px;
      margin: 0 0 6px;
      font-weight: 700;
    }
    .meta {
      font-size: 15px;
      opacity: 0.85;
      line-height: 1.8;
    }
    .mode {
      display: inline-block;
      margin-top: 10px;
      padding: 3px 12px;
      border-radius: 12px;
      font-size: 13px;
      background: rgba(255, 255, 255, 0.18);
    }
  }

  .ce-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ce-card {
    background: rgba(255, 255, 255, 0.96);
    color: #1d2430;
    border-radius: 10px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);

    &.is-waiting,
    &.is-done {
      opacity: 0.72;
    }
    &.is-locked {
      border-left: 6px solid #a8432a;
    }

    .left {
      min-width: 240px;
    }
    .name {
      font-size: 19px;
      font-weight: 700;
      margin: 0 0 4px;
    }
    .facts {
      font-size: 14px;
      color: #5b6774;
    }
    .reason {
      font-size: 14px;
      margin-top: 6px;
    }
    .reason.wait {
      color: #7b6a3a;
    }
    .reason.locked {
      color: #a8432a;
      font-weight: 600;
    }
    .reason.done {
      color: #2f5d50;
    }
  }

  button.enter {
    background: #e2704e;
    color: #fff;
    border: 0;
    border-radius: 6px;
    padding: 12px 28px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    &:hover {
      background: #cf6142;
    }
  }

  .status-pill {
    font-size: 14px;
    padding: 8px 16px;
    border-radius: 6px;
    background: #edeff2;
    color: #5b6774;
  }

  .password-box {
    max-width: 420px;
    margin: 12vh auto 0;
    background: rgba(255, 255, 255, 0.96);
    color: #1d2430;
    border-radius: 10px;
    padding: 28px;
    text-align: center;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);

    h2 {
      font-size: 19px;
      margin: 0 0 6px;
    }
    p {
      font-size: 14px;
      color: #5b6774;
      margin: 0 0 18px;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      font-size: 20px;
      text-align: center;
      letter-spacing: 2px;
      padding: 10px;
      border: 1px solid #cfd6dd;
      border-radius: 6px;
      margin-bottom: 14px;
    }
    button {
      width: 100%;
      background: #e2704e;
      color: #fff;
      border: 0;
      border-radius: 6px;
      padding: 12px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
    }
    .err {
      color: #a8432a;
      font-size: 14px;
      margin-top: 12px;
    }
  }
`;

/** 當天日期，顯示成「2026 年 8 月 29 日（星期六）」 */
function todayWording() {
  const d = new Date();
  const week = ["日", "一", "二", "三", "四", "五", "六"][d.getDay()];
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日（星期${week}）`;
}

const Dashboard = ({ urlKey, handleLoading, savedPassword, onAuthenticated }) => {
  const [password, setPassword] = useState(savedPassword || "");
  const [exam, setExam] = useState(null);
  const [sections, setSections] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  const load = (pw) => {
    setBusy(true);
    setError("");
    return axios
      .post(`${process.env.REACT_APP_EXAM_DASHBOARD}/${urlKey}`, { password: pw })
      .then((res) => {
        const data = res.data;
        if (data.success) {
          setExam(data.exam);
          setSections(data.sections);
          if (onAuthenticated) {
            onAuthenticated(pw);
          }
        } else {
          setError(data.message || "密碼錯誤");
        }
      })
      .catch(() => {
        setError("連線失敗，請稍後再試。");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  // 考完一科回來時，App 還記得密碼——直接重新抓狀態，不用再問一次。
  useEffect(() => {
    if (savedPassword && !exam) {
      load(savedPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedPassword]);

  const submitPassword = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (busy) {
      return;
    }
    load(password);
  };

  /**
   * Confirm 之後真正進場。
   *
   * 用的是總密碼——伺服器兩種密碼都接受（該科原本的，或這場的總密碼），
   * 所以學生只需要記一組。
   */
  const enterSection = (section) => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError("");

    axios
      .post(`${process.env.REACT_APP_GET_EXAM_BY_URLKEY}/${section.urlKey}`, {
        password,
      })
      .then((res) => {
        const data = res.data;
        if (!data.success) {
          setChosen(null);
          setError(data.message || "無法進入這一科，請聯絡教務人員。");
          return;
        }
        handleLoading(
          { ...data.candidate },
          {
            ...data.practice_test,
            pages: JSON.parse(data.practice_test.pages),
            answers: JSON.parse(data.practice_test.answers),
          }
        );
      })
      .catch(() => {
        setChosen(null);
        setError("連線失敗，請稍後再試。");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  // ---- 還沒輸入密碼 ----
  if (!exam) {
    return (
      <StyledDashboard>
        <div className="inner">
          <img className="logo" src={WhiteLogo} alt="Churchill English" />
          <form className="password-box" onSubmit={submitPassword}>
            <h2>請輸入測驗密碼</h2>
            <p>密碼在教務人員寄給你的信件中。</p>
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button type="submit" disabled={busy}>
              {busy ? "確認中…" : "進入"}
            </button>
            {error && <div className="err">{error}</div>}
          </form>
        </div>
      </StyledDashboard>
    );
  }

  // ---- 看某一科的說明 ----
  if (chosen) {
    return (
      <TestInformation
        section={chosen}
        mode={exam.mode}
        onBack={() => setChosen(null)}
        onConfirm={() => enterSection(chosen)}
        busy={busy}
      />
    );
  }

  // ---- 科目卡片 ----
  return (
    <StyledDashboard>
      <div className="inner">
        <img className="logo" src={WhiteLogo} alt="Churchill English" />
        <div className="who">
          <h1>{exam.candidateName}</h1>
          <div className="meta">
            准考證號 {exam.candidateNumber}
            <br />
            {todayWording()}
          </div>
          <div className="mode">{exam.modeWording}</div>
        </div>

        {error && (
          <div className="ce-card" style={{ borderLeft: "6px solid #a8432a" }}>
            <div className="left">
              <div className="reason locked">{error}</div>
            </div>
          </div>
        )}

        <div className="ce-cards">
          {sections.map((s) => (
            <div key={s.id} className={`ce-card is-${s.status}`}>
              <div className="left">
                <p className="name">{s.name}</p>
                <div className="facts">
                  {s.questions ? `${s.questions} 題` : ""}
                  {s.questions && s.minutes ? " ｜ " : ""}
                  {s.minutes ? `${s.minutes} 分鐘` : ""}
                </div>
                {s.status === "waiting" && (
                  <div className="reason wait">{s.reason}</div>
                )}
                {s.status === "locked" && (
                  <div className="reason locked">{s.reason}</div>
                )}
                {s.status === "done" && (
                  <div className="reason done">
                    已完成
                    {s.finishedAt ? `（${s.finishedAt.slice(0, 16)}）` : ""}
                  </div>
                )}
              </div>

              {s.status === "available" ? (
                <button className="enter" onClick={() => setChosen(s)}>
                  開始測驗
                </button>
              ) : (
                <div className="status-pill">
                  {s.status === "done" && "✓ 已交卷"}
                  {s.status === "waiting" && "尚未開放"}
                  {s.status === "locked" && "已鎖定"}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </StyledDashboard>
  );
};

export default Dashboard;
